/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import { define, s, ui } from '@owenscorning/pcb.alpha';
import { useEffect, useState } from 'react';

import { mdms } from '../../../../../api';
import useDeviceBreakpoint from '../../../../../hooks/use_device_breakpoint';
import useReference from '../../../../../hooks/use_reference';
import CMSContentAndImage from '../../../../OC/PageBuilder/ContentAndImage';
import { highlightSettingsSection } from '../../../../OC/PageBuilder/utilities/shared';
import { connectLocation } from '../../../../location/LocationConnectors';
import { Renderer as CardSet } from '../Page/CardSet';

const featureChoices = {
  trudefinition: 'Color Platform',
  warranty_25_year: 'Limited 25 Year Warranty',
  warranty_limited_lifetime: 'Limited Lifetime Warranty',
  premium_color_blends: 'Premium Color Blends',
  sbs: 'SBS Modified Asphalt',
  algae_resistance: 'StreakGuard',
  surenail: 'SureNail',
  weatherguard: 'WeatherGuard Technology',
  wind_resistance: 'Wind Resistance',
};

const getReferenceData = (name, language) => {
  const refObj = {
    __ref: `Cms::Content|{"type":"Reusable","path":"roofing","name":"${name}","language":"${language}"}`,
  };
  const data = useReference(refObj);
  const moduleData = data?.results?.contents?.sections?.[0]?.modules?.[0]?.data;
  return moduleData || {};
};

const getPlainText = delta =>
  delta?.ops?.reduce((text, op) => {
    if (!op.insert) throw new TypeError('only `insert` operations can be transformed!');
    if (typeof op.insert !== 'string') return `${text} `;
    return text + op.insert;
  }, '');

const getContentAndImageDataAsCard = ({ media = null, content = null }) => ({
  image: media?.resources?.image,
  prehead: content?.prehead,
  heading: getPlainText(content?.heading),
  text: content?.bodyCopy,
  cta: content?.ctas?.map(cta => ({ ...cta, variant: 'chevron' })),
  ctaSwitch: true,
});

const getAllData = localeCode => {
  const data = new Map();
  data.set('trudefinition', getReferenceData('Color Platform Feature Benefit', localeCode));
  data.set('warranty_limited_lifetime', getReferenceData('Limited Lifetime Warranty Feature Benefit', localeCode));
  data.set('warranty_25_year', getReferenceData('Limited 25 Year Warranty Feature Benefit', localeCode));
  data.set('wind_resistance', getReferenceData('Wind Resistance Feature Benefit', localeCode));
  data.set('weatherguard', getReferenceData('WeatherGuard Technology Feature Benefit', localeCode));
  data.set('premium_color_blends', getReferenceData('Premium Color Blends Feature Benefit', localeCode));
  data.set('algae_resistance', getReferenceData('StreakGuard Feature Benefit', localeCode));
  data.set('surenail', getReferenceData('SureNail Feature Benefit', localeCode));
  data.set('sbs', getReferenceData('SBS Modified Asphalt Feature Benefit', localeCode));
  return data;
};

const ContentAndImageContainer = styled.div`
  margin-bottom: 64px;
  & .content h3 {
    font-size: 28px;
    line-height: 30px;
  }
`;

const buildAndAddCard = (data, benefitName, cards, featured) => {
  const card = getContentAndImageDataAsCard(data.get(benefitName));
  if (benefitName === featured) {
    return card;
  }
  cards.push(card);
  return null;
};

const buildCards = (attributes, data, featured, showPremiumColorBlends) => {
  // build list of cards and keep track of existence of a featured cards without adding it
  const cards = [];
  const featureKeys = ['trudefinition', 'wind_resistance', 'weatherguard', 'surenail', 'sbs', 'algae_resistance'];
  let featuredCard = null;

  // Add warranty first
  if (attributes?.warranty === 'limited-lifetime') {
    featuredCard = buildAndAddCard(data, 'warranty_limited_lifetime', cards, featured) ?? featuredCard;
  } else if (attributes?.warranty === '25-year') {
    featuredCard = buildAndAddCard(data, 'warranty_25_year', cards, featured) ?? featuredCard;
  }

  // Map through any simple feature keys
  featureKeys.forEach(key => {
    if (attributes?.[key]) {
      featuredCard = buildAndAddCard(data, key, cards, featured) ?? featuredCard;
    }
  });

  // Premium Color Blends last
  if (showPremiumColorBlends) {
    featuredCard = buildAndAddCard(data, 'premium_color_blends', cards, featured) ?? featuredCard;
  }

  return [cards, featuredCard];
};

const ShingleFeaturesAndBenefits = connectLocation(({ attributes, featured, locale: { code }, zip }) => {
  const [showPremiumColorBlends, setShowPremiumColorBlends] = useState(false);
  const data = getAllData(code);

  useEffect(() => {
    const pageSlug = Board?.build?.slug;
    mdms
      .get(`/api/v2/product/shingles/${pageSlug}?zip=${zip}&lang=${code}`)
      .then(response => response.json())
      .then(responseJson => {
        setShowPremiumColorBlends(responseJson?.shingle_colors?.find(color => color?.premium_color_blend));
      });
  }, []);

  const [cards, featuredCard] = buildCards(attributes, data, featured, showPremiumColorBlends);

  const featuredData = featuredCard ? data.get(featured) : null;

  const { isPhone, isTablet } = useDeviceBreakpoint();

  const cardSetSettings = {
    itemsPerRow: isPhone ? 1 : isTablet ? 2 : 4,
    cardSize: 'md',
    aspectRatio: { selection: 'custom', custom: 1.44 },
  };

  return (
    <>
      {featuredData && (
        <ContentAndImageContainer>
          <CMSContentAndImage {...featuredData} />
        </ContentAndImageContainer>
      )}
      <CardSet cards={cards} settings={cardSetSettings} carousel={{ enabled: true }} />
    </>
  );
});

export default define`Shingle Features and Benefits`('0.0.1')({
  thumbnail: ui`Modules/Page/Thumbnails/Modules/Page/Thumbnails/Placeholder`,
  view: ({ Board, value }) => (
    <ShingleFeaturesAndBenefits featured={value?.featured} attributes={Board?.settings?.attributes} />
  ),
  edit: ui`Form`.of({
    featured: ui`Choices`.of({
      ...featureChoices,
    })({
      label: 'Featured Content',
      default: 'trudefinition',
      mode: ui`Choices/Mode/Dropdown`,
    }),
    [s._]: ui`Tip`.of(
      'The cards displayed here are mostly determined by Attributes with the exception of Premium Color Blends, which appears when present on any Shingle Colors.'
    ),
    [s._]: ui`RawLink`.of('Go to settings')({
      onClick: () => {
        highlightSettingsSection('sidebar-pane', 'attributes');
      },
      noTopMargin: true,
      icon: 'external-link',
    }),
  }),
});
